import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";

const COPY = {
  en: {
    title: "Updated platform documents",
    body: "Please review and accept the current documents before continuing. Your acceptance records the exact versions and language shown here.",
    checkbox: "I have reviewed and accept the User Agreement, Community Rules and Moderation Policy, and acknowledge the Privacy Policy.",
    ruCheckbox: "I have reviewed and accept the Russian Public Offer and acknowledge the Russian Privacy and Personal Data Policy.",
    ruConsent: "I separately consent to the processing of my personal data under the Russian Personal Data Consent.",
    accept: "Accept and continue",
    accepting: "Saving…",
    error: "The documents could not be loaded. Try again.",
    retry: "Try again",
  },
  ru: {
    title: "Обновлённые документы платформы",
    body: "Пожалуйста, ознакомьтесь с актуальными документами и примите их, чтобы продолжить. Мы сохраним точные версии и язык, показанные здесь.",
    checkbox: "Я ознакомился и принимаю Пользовательское соглашение, Правила сообщества и Политику модерации, а также подтверждаю ознакомление с Политикой конфиденциальности.",
    ruCheckbox: "Я ознакомился(-ась) и принимаю Публичную оферту, а также подтверждаю ознакомление с Политикой конфиденциальности и обработки персональных данных.",
    ruConsent: "Я отдельно даю Согласие на обработку персональных данных.",
    accept: "Принять и продолжить",
    accepting: "Сохраняем…",
    error: "Не удалось загрузить документы. Попробуйте ещё раз.",
    retry: "Повторить",
  },
  sk: {
    title: "Aktualizované dokumenty platformy",
    body: "Pred pokračovaním si prečítajte a prijmite aktuálne dokumenty. Zaznamenáme presné verzie a jazyk, ktoré sú tu zobrazené.",
    checkbox: "Prečítal/a som si a prijímam Používateľskú zmluvu, Pravidlá komunity a Zásady moderovania a beriem na vedomie Zásady ochrany osobných údajov.",
    ruCheckbox: "Prečítal/a som si a prijímam ruskú Verejnú ponuku a beriem na vedomie ruské Zásady ochrany osobných údajov.",
    ruConsent: "Samostatne súhlasím so spracúvaním osobných údajov podľa ruského Súhlasu so spracúvaním osobných údajov.",
    accept: "Prijať a pokračovať",
    accepting: "Ukladá sa…",
    error: "Dokumenty sa nepodarilo načítať. Skúste to znova.",
    retry: "Skúsiť znova",
  },
};

const PATHS = {
  terms: "/legal/terms?from=reaccept",
  community_rules: "/legal/community-rules?from=reaccept",
  privacy: "/legal/privacy?from=reaccept",
  moderation_policy: "/legal/moderation?from=reaccept",
  ru_user_agreement: "/legal/user-agreement?from=reaccept",
  ru_public_offer: "/legal/terms?from=reaccept",
  ru_privacy: "/legal/privacy?from=reaccept",
  ru_pd_consent: "/legal/personal-data-consent?from=reaccept",
};

const languageCode = (value) => {
  const code = String(value || "").toLowerCase().split("-")[0];
  return ["en", "ru", "sk"].includes(code) ? code : "en";
};

export default function LegalAcceptanceGate({ onAccepted }) {
  const { i18n } = useTranslation();
  const language = languageCode(i18n.language);
  const copy = COPY[language] || COPY.en;
  const [bundle, setBundle] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [personalDataConsentAccepted, setPersonalDataConsentAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api
      .get("/accounts/legal/documents/", {
        params: { language },
        suppressGlobalError: true,
        suppressUnauthorizedRedirect: true,
      })
      .then((response) => {
        if (active) setBundle(response.data || null);
      })
      .catch(() => {
        if (active) setError(copy.error);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [copy.error, language, reloadKey]);

  const submit = async () => {
    const isRussianBundle = bundle?.contour === "RU";
    if (
      !accepted ||
      (isRussianBundle && !personalDataConsentAccepted) ||
      !bundle?.bundle_id ||
      saving
    ) return;
    setSaving(true);
    setError("");
    try {
      await api.post(
        "/accounts/legal/accept/",
        {
          bundle_id: bundle.bundle_id,
          language,
          source_path: window.location.pathname || "/",
          personal_data_consent_accepted: isRussianBundle && personalDataConsentAccepted,
        },
        { suppressGlobalError: true }
      );
      onAccepted?.();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || copy.error);
      if (requestError?.response?.data?.code === "legal_bundle_outdated") {
        setBundle(null);
        setAccepted(false);
        setPersonalDataConsentAccepted(false);
        setReloadKey((value) => value + 1);
      }
    } finally {
      setSaving(false);
    }
  };

  const isRussianBundle = bundle?.contour === "RU";

  return (
    <div className="fixed inset-0 z-[9997] flex min-h-[100dvh] items-center justify-center overflow-y-auto bg-gradient-to-b from-stone-950 to-slate-950 px-4 py-8 text-white">
      <main className="w-full max-w-2xl rounded-3xl border border-white/10 bg-black/45 p-6 shadow-[0_18px_70px_rgba(0,0,0,0.55)] sm:p-8">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{copy.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-zinc-300 sm:text-base">{copy.body}</p>

        {loading ? <div className="mt-7 h-28 animate-pulse rounded-2xl bg-white/5" /> : null}
        {bundle ? (
          <div className="mt-7 grid gap-3 sm:grid-cols-2">
            {(bundle.documents || []).map((document) => (
              <a
                key={document.document_type}
                href={PATHS[document.document_type]}
                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 transition hover:bg-white/10"
              >
                <span className="block font-medium text-sky-200">{document.title}</span>
                <span className="mt-1 block text-xs text-zinc-400">{document.version}</span>
              </a>
            ))}
          </div>
        ) : null}

        {error ? (
          <div className="mt-5 rounded-xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {error}
            {!bundle ? (
              <button type="button" onClick={() => setReloadKey((value) => value + 1)} className="ml-2 underline">
                {copy.retry}
              </button>
            ) : null}
          </div>
        ) : null}

        <label className="mt-7 flex cursor-pointer items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm leading-relaxed text-zinc-200">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
            disabled={!bundle || saving}
            className="mt-1 h-4 w-4 shrink-0 accent-blue-500"
          />
          <span>{isRussianBundle ? copy.ruCheckbox : copy.checkbox}</span>
        </label>

        {isRussianBundle ? (
          <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm leading-relaxed text-zinc-200">
            <input
              type="checkbox"
              checked={personalDataConsentAccepted}
              onChange={(event) => setPersonalDataConsentAccepted(event.target.checked)}
              disabled={!bundle || saving}
              className="mt-1 h-4 w-4 shrink-0 accent-blue-500"
            />
            <span>{copy.ruConsent}</span>
          </label>
        ) : null}

        <button
          type="button"
          onClick={submit}
          disabled={
            !accepted ||
            (isRussianBundle && !personalDataConsentAccepted) ||
            !bundle ||
            saving
          }
          className="mt-6 h-12 w-full rounded-full bg-[#3F73FF] px-5 font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-45"
        >
          {saving ? copy.accepting : copy.accept}
        </button>
      </main>
    </div>
  );
}
